import { Dayjs } from "dayjs";

type DayKey = string; // "YYYY-MM-DD"
interface DayCell {
  rows: (string | null)[];
}
type Grid = Record<DayKey, DayCell>;

export class TimespanGrid {
  public grid: Grid = {};

  // Fast indexes
  private dayToRows: Map<DayKey, Set<number>> = new Map();
  private rowToDays: Map<number, Set<DayKey>> = new Map();
  private idToRows: Map<string, Set<number>> = new Map();
  private idToDays: Map<string, Set<DayKey>> = new Map();

  // -------- helpers --------
  private fmt(d: Dayjs): DayKey {
    return d.format("YYYY-MM-DD");
  }

  private datesInRange(start: Dayjs, end: Dayjs): DayKey[] {
    if (end.isBefore(start, "day")) {
      throw new Error("end < start");
    }
    const out: DayKey[] = [];
    let cur = start.startOf("day");
    const stop = end.startOf("day");
    while (!cur.isAfter(stop, "day")) {
      out.push(this.fmt(cur));
      cur = cur.add(1, "day");
    }
    return out;
  }

  private ensureDay(key: DayKey): DayCell {
    if (!this.grid[key]) {
      this.grid[key] = { rows: [] };
    }
    return this.grid[key];
  }

  private ensureDayIndex(key: DayKey): Set<number> {
    let s = this.dayToRows.get(key);
    if (!s) {
      s = new Set<number>();
      this.dayToRows.set(key, s);
    }
    return s;
  }

  private ensureRowIndex(rowIdx: number): Set<DayKey> {
    let s = this.rowToDays.get(rowIdx);
    if (!s) {
      s = new Set<DayKey>();
      this.rowToDays.set(rowIdx, s);
    }
    return s;
  }

  private ensureIdRows(id: string): Set<number> {
    let s = this.idToRows.get(id);
    if (!s) {
      s = new Set<number>();
      this.idToRows.set(id, s);
    }
    return s;
  }

  private ensureIdDays(id: string): Set<DayKey> {
    let s = this.idToDays.get(id);
    if (!s) {
      s = new Set<DayKey>();
      this.idToDays.set(id, s);
    }
    return s;
  }

  /**
   * Check whether the given row can host `id` for all dates.
   * Allows the slot to already contain `id`, but rejects any other id.
   */
  private rowFitsForId(dates: DayKey[], rowIdx: number, id: string): boolean {
    for (const key of dates) {
      const cell = this.grid[key];
      if (!cell) continue; // day not present => free
      const slot = cell.rows[rowIdx];
      if (slot !== undefined && slot !== null && slot !== id) {
        return false;
      }
    }
    return true;
  }

  /**
   * Union of rows occupied on any of the given days.
   * Used to quickly find the first free row.
   */
  private unionBlockedRows(dates: DayKey[]): Set<number> {
    const blocked = new Set<number>();
    for (const key of dates) {
      const dayRows = this.dayToRows.get(key);
      if (!dayRows) continue;
      for (const r of dayRows) blocked.add(r);
    }
    return blocked;
  }

  private findFirstFreeRow(minRow: number, blocked: Set<number>): number {
    let r = Math.max(0, minRow);
    while (blocked.has(r)) r += 1;
    return r;
  }

  /**
   * Writes the id into the fixed row across all dates and updates all indexes.
   */
  private placeSpan(dates: DayKey[], rowIdx: number, id: string): void {
    const rowDays = this.ensureRowIndex(rowIdx);
    const idDays = this.ensureIdDays(id);
    const idRows = this.ensureIdRows(id);

    for (const key of dates) {
      const cell = this.ensureDay(key);

      // Expand array to preserve index
      while (cell.rows.length <= rowIdx) {
        cell.rows.push(null);
      }

      // If cell already has the same id, we leave it. Otherwise we assign it.
      if (cell.rows[rowIdx] === null || cell.rows[rowIdx] === undefined) {
        cell.rows[rowIdx] = id;
      } else if (cell.rows[rowIdx] !== id) {
        // Should never happen if caller checked rowFitsForId
        throw new Error("Internal: collision while placing span");
      }

      // Update indexes
      this.ensureDayIndex(key).add(rowIdx);
      rowDays.add(key);
      idDays.add(key);
    }

    idRows.add(rowIdx);
  }

  // -------- public API --------

  /**
   * Add an inclusive timespan [start, end] for `id`.
   * Tries to reuse an existing row for the same id if collision-free and >= minRow.
   * Otherwise selects the first free row >= minRow.
   * Returns the row index used for this addition.
   *
   * Note: If reuse is impossible (collision or minRow constraint), the same id can end up
   * on another row for the new segment. This keeps placement O(D) without costly relocations.
   */
  public addTimespan(start: Dayjs, end: Dayjs, id: string, minRow: number = 0): number {
    const dates = this.datesInRange(start, end);

    // 1) Try to reuse an existing row for this id, preferring the lowest row
    const existingRows = Array.from(this.idToRows.get(id) ?? []).sort((a, b) => a - b);
    for (const r of existingRows) {
      if (r >= minRow && this.rowFitsForId(dates, r, id)) {
        this.placeSpan(dates, r, id);
        return r;
      }
    }

    // 2) No reusable row fits, pick the first free row using union of blocked rows
    const blocked = this.unionBlockedRows(dates);
    const rowIdx = this.findFirstFreeRow(minRow, blocked);
    this.placeSpan(dates, rowIdx, id);
    return rowIdx;
  }

  public getRow(start: Dayjs, end: Dayjs, id: string): number | null {
    const dates = this.datesInRange(start, end);

    // If we never placed this id, there is no row to return
    const candidateRows = this.idToRows.get(id);
    if (!candidateRows || candidateRows.size === 0) {
      return null;
    }

    // Prefer the lowest row that covers the entire range
    const rowsSorted = Array.from(candidateRows).sort((a, b) => a - b);

    for (const r of rowsSorted) {
      let coversAllDays = true;
      for (const key of dates) {
        const cell = this.grid[key];
        const val = cell?.rows?.[r];
        if (val !== id) {
          coversAllDays = false;
          break;
        }
      }
      if (coversAllDays) return r;
    }

    return null;
  }

  /**
   * Remove an id across all its days. Returns affected day keys.
   * Uses id indexes for O(R * D_id) rather than scanning the whole grid.
   */
  public removeById(id: string): DayKey[] {
    const idDays = this.idToDays.get(id);
    const idRows = this.idToRows.get(id);
    if (!idDays || idDays.size === 0 || !idRows || idRows.size === 0) {
      // Nothing to remove
      this.idToDays.delete(id);
      this.idToRows.delete(id);
      return [];
    }

    const affected: DayKey[] = [];

    for (const day of idDays) {
      const cell = this.grid[day];
      if (!cell) continue;
      let changed = false;

      for (const r of idRows) {
        if (cell.rows[r] === id) {
          cell.rows[r] = null;
          changed = true;

          // Update dayToRows and rowToDays
          const dayRows = this.dayToRows.get(day);
          dayRows?.delete(r);
          if (dayRows && dayRows.size === 0) {
            this.dayToRows.delete(day);
          }

          const rowDays = this.rowToDays.get(r);
          rowDays?.delete(day);
          if (rowDays && rowDays.size === 0) {
            this.rowToDays.delete(r);
          }
        }
      }

      if (changed) {
        affected.push(day);
        // Trim trailing nulls to keep rows compact
        while (cell.rows.length > 0 && cell.rows[cell.rows.length - 1] === null) {
          cell.rows.pop();
        }
        if (cell.rows.length === 0) {
          delete this.grid[day];
        }
      }
    }

    // Clear id indexes
    this.idToDays.delete(id);
    this.idToRows.delete(id);

    return affected;
  }

  // -------- optional utilities --------

  /** Get all row indices occupied on a given day. */
  public getOccupiedRowsForDay(day: DayKey): number[] {
    return Array.from(this.dayToRows.get(day) ?? []).sort((a, b) => a - b);
  }

  /** Get all day keys occupied in a given row. */
  public getOccupiedDaysForRow(rowIdx: number): DayKey[] {
    return Array.from(this.rowToDays.get(rowIdx) ?? []).sort();
  }

  /** Get all rows currently used by a given id. */
  public getRowsForId(id: string): number[] {
    return Array.from(this.idToRows.get(id) ?? []).sort((a, b) => a - b);
  }

  /** Get all days currently used by a given id. */
  public getDaysForId(id: string): DayKey[] {
    return Array.from(this.idToDays.get(id) ?? []).sort();
  }
}
